import type { ReactNode } from 'react';

import {
  AccordionChevron,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
} from '@/lib/base/accordion';
import { cx } from '@/utils/cx';

interface MeetingPrepAccordionSectionProps {
  title: string;
  defaultExpanded?: boolean;
  metadata?: string;
  badges?: ReactNode;
  children: ReactNode;
  showBorder?: boolean;
  sectionId?: string;
}

/**
 * Accordion section used by the meeting-prep record sections (people,
 * company intel, deal status, summary, previous interactions). Composes
 * the shared `<AccordionItem>` for smooth animation + react-aria a11y,
 * with three wrapper-specific conventions:
 *
 *  - Optional bottom border between sections (`showBorder`, default true)
 *  - Slightly larger `py-3` trigger padding
 *  - Renders `metadata` + `badges` in the trigger row alongside the title
 *  - Chevron points down when collapsed and rotates up when expanded
 *    (the design-system default `AccordionChevron` variant)
 *
 * Uses the overlay-trigger pattern so the whole row is clickable to
 * toggle. Title content has no nested interactives, so badges/metadata
 * just sit inside the visible content layer with `pointer-events-none`.
 */
export function MeetingPrepAccordionSection({
  title,
  defaultExpanded,
  metadata,
  badges,
  children,
  showBorder = true,
  sectionId,
}: MeetingPrepAccordionSectionProps) {
  return (
    <AccordionItem
      id={sectionId}
      defaultExpanded={defaultExpanded ?? true}
      className={cx(
        showBorder && 'border-b border-[var(--color-border-secondary)]'
      )}
    >
      <div className='relative'>
        <AccordionTrigger
          aria-label={title}
          className='absolute inset-0 w-full focus-visible:ring-inset rounded'
        >
          {null}
        </AccordionTrigger>

        <div className='pointer-events-none relative flex flex-1 min-w-0 items-center gap-2 py-3'>
          <h3 className='text-xs md:text-sm font-medium text-[var(--color-text-primary)]'>
            {title}
          </h3>
          {badges}
          {metadata && (
            <span className='ml-auto text-xs font-normal text-[var(--color-text-tertiary)]'>
              {metadata}
            </span>
          )}
          <AccordionChevron
            variant='default'
            className={cx('w-5 h-5 text-fg-tertiary', !metadata && 'ml-auto')}
          />
        </div>
      </div>
      {/* pb-4 gives the panel content breathing room above the section's
          bottom border. Without it the last line (e.g. the final "Focus
          areas" bullet) butts directly against `border-b`. Bottom padding on
          the panel's inner wrapper is clipped while collapsed, so it only
          takes height when the section is open. */}
      <AccordionPanel className='pt-2 pb-4'>{children}</AccordionPanel>
    </AccordionItem>
  );
}
